// Patent claims exercised: P-001 Claims 11-14 | P-003 Claims 1-3
import { API_BASE } from "../config";
import React, { useState } from 'react';
import XaiPromptPanel from './XaiPromptPanel';
import { CitationsButton } from './Citations';
import { citationCount } from '../utils/xaiHelpers';
import MedicalDisclaimer from './MedicalDisclaimer';
import { handleAIError } from '../utils/aiErrorHandler';
import CostChip from './CostChip';
import { useCreditSpend } from '../contexts/CreditSpendContext';

const AINutritionistChat = ({ fetchWithAuth }) => {
  const { spend } = useCreditSpend();  // #77: connection greeting = free; a real question = ai_chat (base 1, router may price higher server-side)
  const [aiChat, setAiChat] = useState({
    input: 'Confirm connection to AI Health Advisor',
    response: '',
    selectedOption: 'confirm',
    promptData: null,
    showXai: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleOptionChange = (option) => {
    if (option === 'confirm') {
      setAiChat(prev => ({
        ...prev,
        input: 'Confirm connection to AI Health Advisor',
        selectedOption: option
      }));
    } else if (option === 'recommendations') {
      setAiChat(prev => ({
        ...prev,
        input: 'Please review all of my health information and provide generalized nutrition and health recommendations based on my complete profile.',
        selectedOption: option
      }));
    } else {
      setAiChat(prev => ({
        ...prev,
        input: '',
        selectedOption: option
      }));
    }
  };

  const handleAiChatSubmit = async () => {
    if (!aiChat.input.trim()) return;

    setIsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/chat/ask`, {
        method: 'POST',
        body: JSON.stringify({
          message: aiChat.input,
          // MUST be 'general_conversation' — this is what routes /chat/ask to
          // chat_service.general_health_conversation (chat.py:87), whose docstring reads
          // "Handle generalized health and nutrition conversation for the Dashboard chat" and
          // which uses the medication/condition/allergen-aware conversational system prompt.
          //
          // This read 'general' until 2026-08-21. 'general' is a valid enum value but matches no
          // branch, so EVERY message fell through to generate_food_recommendations — the recipe
          // generator, whose prompt forces "ONLY valid JSON {recommendations:[{type:recipe…}]}"
          // (chat_service.py:501). That schema has no field for a warning, so the advisor answered
          // safety questions with recipe cards and could never surface a UHC check. Verified live
          // on Prod: a user on amlodipine with celiac disease and eGFR 38 asked about a grapefruit
          // salad with whole wheat bread and was RECOMMENDED it, while the deterministic gate
          // returned severity=avoid on all three counts. See backlog #95.
          context_type: 'general_conversation',
          chat_mode: aiChat.selectedOption || 'other'
        })
      });

      if (response.ok) {
        const data = await response.json();

        let responseText = '';
        const resp = data.response;

        // Handle off-topic rejection
        if (resp && typeof resp === 'object' && resp.off_topic) {
          responseText = resp.message || 'I can only assist with nutrition, health, and wellness topics. Please ask me something related to your diet, nutrition, or health goals.';
          // Enrich prompt_data with domain validation rejection for XAI transparency
          const enrichedPromptData = {
            ...(data.prompt_data || {}),
            domain_validation: {
              result: 'REJECTED',
              domain: 'food/health/wellness',
              entity_type: 'chat_query',
              reason: 'Query is not related to food, nutrition, health, wellness, fitness, or dietary topics.',
              input_text_preview: (data.prompt_data?.user_message || '').substring(0, 100)
            }
          };
          setAiChat(prev => ({
            ...prev,
            response: responseText,
            input: 'Ask another question...',
            promptData: enrichedPromptData,
            showXai: false
          }));
          return;
        }

        // Extract main text from response
        if (typeof resp === 'string') {
          responseText = resp;
        } else if (typeof resp === 'object' && resp !== null) {
          // Pull out the text response first
          if (resp.response) responseText = resp.response;
          else if (resp.message) responseText = resp.message;
          else if (resp.summary) responseText = resp.summary;

          // Format recommendations array if present (in response or top-level)
          const recs = resp.recommendations || data.recommendations;
          if (Array.isArray(recs) && recs.length > 0) {
            if (responseText) responseText += '\n\n';
            responseText += 'Recommendations:\n';
            recs.forEach((rec, idx) => {
              const name = rec.title || rec.name || rec.meal_name || `Recommendation ${idx + 1}`;
              responseText += `\n${idx + 1}. ${name}\n`;
              if (rec.description) responseText += `   ${rec.description}\n`;
              if (rec.health_benefits && Array.isArray(rec.health_benefits)) {
                rec.health_benefits.forEach(b => { responseText += `   • ${b}\n`; });
              }
              if (rec.calories_per_serving) responseText += `   Calories: ${rec.calories_per_serving}\n`;
              if (rec.category) responseText += `   Category: ${rec.category}\n`;
              if (rec.prep_time_minutes || rec['prep_time_min/hr']) {
                responseText += `   Prep time: ${rec.prep_time_minutes || rec['prep_time_min/hr']} min\n`;
              }
            });
          }

          // If still empty, format any remaining object keys as readable text
          if (!responseText) {
            const lines = [];
            for (const [key, val] of Object.entries(resp)) {
              if (key === 'recommendations' || key === 'success' || key === 'off_topic') continue;
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              if (typeof val === 'string') lines.push(`${label}: ${val}`);
              else if (typeof val === 'number') lines.push(`${label}: ${val}`);
              else if (Array.isArray(val)) lines.push(`${label}:\n${val.map(v => `  • ${typeof v === 'string' ? v : JSON.stringify(v)}`).join('\n')}`);
            }
            responseText = lines.join('\n\n');
          }
        }

        if (!responseText) responseText = data.message || 'Response received.';

        setAiChat(prev => ({
          ...prev,
          response: responseText,
          input: 'Ask another question...',
          promptData: data.prompt_data || null,
          showXai: false
        }));
      } else if (await handleAIError(response)) {
        // unified AI limit modal shown
      }
    } catch (error) {
      console.error('AI Chat error:', error);
      setAiChat(prev => ({
        ...prev,
        response: 'Sorry, there was an error processing your request. Please try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const isTextareaLocked = aiChat.selectedOption === 'confirm' || aiChat.selectedOption === 'recommendations';

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">AI Health Advisor</h3>
      </div>
      <div className="ai-chat-container">
        <div className="welcome-message">
          <h4>Welcome to your AI Health Advisor!</h4>
          <p>Ask me anything about nutrition, recipes, meal planning, or health advice based on your profile.</p>

          <div className="radio-group">
            <label className="form-radio">
              <input
                type="radio"
                name="chatOption"
                value="confirm"
                checked={aiChat.selectedOption === 'confirm'}
                onChange={() => handleOptionChange('confirm')}
              />
              <span>Confirm connection to Assistant</span>
            </label>

            <label className="form-radio">
              <input
                type="radio"
                name="chatOption"
                value="recommendations"
                checked={aiChat.selectedOption === 'recommendations'}
                onChange={() => handleOptionChange('recommendations')}
              />
              <span>Make some generalized recommendations based on all information in my profile</span>
            </label>

            <label className="form-radio">
              <input
                type="radio"
                name="chatOption"
                value="other"
                checked={aiChat.selectedOption === 'other'}
                onChange={() => handleOptionChange('other')}
              />
              <span>Other Health/Nutrition related conversation</span>
            </label>
          </div>
        </div>

        <MedicalDisclaimer variant="ai" />

        <div className="form-group">
          <label className="form-label">Input AI / Chat Message:</label>
          <textarea
            className={`form-textarea ai-chat-input ${isTextareaLocked ? 'ainc-textarea--locked' : ''}`}
            rows="5"
            value={aiChat.input}
            onChange={(e) => {
              if (!isTextareaLocked) {
                setAiChat(prev => ({ ...prev, input: e.target.value }));
              }
            }}
            onFocus={(e) => {
              if (!isTextareaLocked && e.target.value === 'Ask another question...') {
                e.target.select();
              }
            }}
            readOnly={isTextareaLocked}
            placeholder="Type your message to the AI nutritionist..."
          />
        </div>

        <div className="form-group text-center">
          <button
            className="btn btn-primary"
            onClick={() => spend(aiChat.selectedOption === 'confirm' ? 'connection_greeting' : 'ai_chat', 'Send this message?', handleAiChatSubmit)}
            disabled={!aiChat.input.trim() || isLoading}
          >
            Submit Chat <CostChip apiType={aiChat.selectedOption === 'confirm' ? 'connection_greeting' : 'ai_chat'} />
          </button>
        </div>

        {/* Spinner overlay while loading */}
        {isLoading && (
          <div className="ainc-spinner-container">
            <div className="ainc-spinner"></div>
            <p className="ainc-spinner-text">Your AI Health Advisor is thinking...</p>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">AI Response:</label>
          <textarea
            className="form-textarea ai-chat-response"
            rows="10"
            value={aiChat.response}
            readOnly
            placeholder="AI response will appear here..."
          />
          <div className="ai-chat-attribution">
            Powered by AWS Bedrock / Anthropic AI
          </div>
          {aiChat.promptData && (
            <>
              <CitationsButton
                open={aiChat.showXai}
                count={citationCount(aiChat.promptData, 'AI Health Advisor')}
                onToggle={() => setAiChat(prev => ({ ...prev, showXai: !prev.showXai }))}
              />
              {aiChat.showXai && <XaiPromptPanel promptData={aiChat.promptData} featureName="AI Health Advisor" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AINutritionistChat;
